import {
  Box,
  Button,
  Heading,
  Stack,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import React, { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getDocumentByUUID } from "../../../../api/documentsService";
import { updateStatusForTimeline } from "../../../../api/timelineService";
import useCustomToast from "../../../../customHooks/useCustomToast";
import { setDocumentsFromApiCall } from "../../../../Redux/documentsSlice";
import {
  cancelUpload,
  initiateCancelUploadProcess,
  keepUploading,
  retryUpload,
} from "../../../../utils/cancelFileUpload";
import { uploadFileWithProgress } from "../../../../utils/UTILITY_METHODS";
import FileUpload from "../../../FileUpload";
import CancelFileUploadModal from "../../../Modals/CancelFileUploadModal";
import UploadProgress from "../../../UploadProgress";
import SingleFinancialStatement from "./SingleFinancialStatement";

const FinancialStatements = ({ handleUserTabClicked }) => {
  const { financialStatements } = useSelector(
    (store) => store.documentsDetails
  );
  const [financialUploadSuccess, setFinancialUploadSuccess] = useState(false);
  const [financialUploadProgress, setFinancialUploadProgress] = useState(0);
  const [isFinancialUploading, setIsFinancialUploading] = useState(false);
  const [financialUploadFailed, setFinancialUploadFailed] = useState(false);
  const [lastFinancialFile, setLastFinancialFile] = useState(null);
  const dispatch = useDispatch();
  const showToast = useCustomToast();

  // Create a ref to store the AbortController
  const abortControllerRef = useRef(null);

  // State for the cancel confirmation modal
  const {
    isOpen: isModalOpen,
    onOpen: openModal,
    onClose: closeModal,
  } = useDisclosure();

  // Financial Document Upload
  const handleFinancialUpload = async (files) => {
    const file = files[0];
    setLastFinancialFile(file); // Store file for retry
    setFinancialUploadFailed(false); // Reset failure state
    setIsFinancialUploading(true); // Show uploading state

    // Initialize a new AbortController
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      const data = await uploadFileWithProgress(
        file,
        setFinancialUploadProgress,
        setFinancialUploadFailed,
        setFinancialUploadSuccess,
        {
          documentType: "financial_statement",
          documentSubType: "financial_statement",
          documentNumber: null,
          documentNumberIdentifier: null,
          level: "user",
        },
        abortController.signal // Pass the signal
      );
      setIsFinancialUploading(false);
      setFinancialUploadProgress(0);
      showToast(data.message, "success");
      if (isModalOpen) closeModal();
      const financialDocumentUUID = data.data;
      const documentDetails = await getDocumentByUUID(financialDocumentUUID);
      dispatch(setDocumentsFromApiCall([documentDetails.data]));
      abortControllerRef.current = null; // Reset the controller
    } catch (error) {
      if (error.message === "Upload canceled") {
        showToast("Upload canceled", "warning");
      } else {
        showToast(error.message || "An error occurred during upload", "error");
      }
      setIsFinancialUploading(false);
      abortControllerRef.current = null; // Reset the controller
    }
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      minHeight="100px" // Ensure there's a minimum height to avoid content squishing
      maxHeight="auto" // Allow the component to grow as necessary
    >
      <Box>
        <Heading as="h2" size="md">
          Financial Documents
        </Heading>
        <Text color={"gray.600"} fontSize="sm" mt={2}>
          Upload your most recent financial statements covering the last fiscal
          year, including all assets, liabilities, income, and expenses.
        </Text>
        <Stack direction={{ base: "column", md: "row" }} spacing={2} mt={5}>
          <FileUpload
            onFileUpload={handleFinancialUpload}
            width={"100%"}
            isUploading={isFinancialUploading}
            uploadFailed={financialUploadFailed}
            uploadSuccess={financialUploadSuccess}
            isIdentificationDocument={false}
            isFinancialDocument={true}
            acceptImageOrPdf={false}
          />
        </Stack>

        {/* Financial Document Upload Progress */}
        {isFinancialUploading && (
          <UploadProgress
            documentType="Financial Document"
            isUploading={isFinancialUploading}
            uploadProgress={financialUploadProgress}
            uploadFailed={financialUploadFailed}
            initiateCancelUploadProcess={() =>
              initiateCancelUploadProcess(abortControllerRef, openModal)
            }
            onRetry={() =>
              retryUpload(
                financialUploadFailed,
                lastFinancialFile,
                handleFinancialUpload
              )
            }
          />
        )}
      </Box>

      {/* Cancel Upload Confirmation Modal */}
      <CancelFileUploadModal
        isModalOpen={isModalOpen}
        closeModal={closeModal}
        cancelUpload={() =>
          cancelUpload(
            abortControllerRef,
            setIsFinancialUploading,
            setFinancialUploadProgress,
            closeModal
          )
        }
        keepUploading={() => keepUploading(closeModal)}
      />

      <Stack spacing={3} mt={5}>
        {financialStatements.map((document, index) => (
          <SingleFinancialStatement
            key={document.uuid || index}
            name={
              document?.original_filename.length > 20
                ? `${document?.original_filename.substring(0, 20)}...`
                : document?.original_filename
            }
            isLast={index === financialStatements.length - 1}
            scannedCopyDetected={index === 2}
            detectedMessage={"Scanned Copy Detected"}
          />
        ))}
      </Stack>

      <Box display={"flex"} justifyContent={"flex-end"} columnGap={4} mt={5}>
        <Button
          colorScheme="blackAlpha"
          color={"blackAlpha.700"}
          size="sm"
          variant="outline"
          px={12}
          onClick={() => handleUserTabClicked(1)}
        >
          Back
        </Button>
        <Button
          colorScheme={
            !Boolean(financialStatements.length) ? "blackAlpha" : "teal"
          }
          color={!Boolean(financialStatements.length) ? "blackAlpha" : "white"}
          size="sm"
          variant="solid"
          px={12}
          disabled={!Boolean(financialStatements.length)}
          onClick={() => {
            handleUserTabClicked(3);
            //update user timeline on server
            updateStatusForTimeline(
              5,
              "financial_information",
              "financial_statements",
              "completed"
            );
          }}
        >
          Next
        </Button>
      </Box>
    </Box>
  );
};

export default FinancialStatements;
